import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { authService } from '../services';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PublicNav from '../components/layout/PublicNav';

export default function RegisterPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const t = useT();
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error: err } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: { full_name: form.full_name },
          emailRedirectTo: `${window.location.origin}/setup`,
        },
      });
      if (err) throw err;

      if (!data.session) {
        // Email confirmation is enabled — user must click the link first.
        // onAuthStateChange in App.jsx will handle sync after confirmation.
        setConfirmSent(true);
        return;
      }

      // Email confirmation disabled — session is available immediately.
      const user = await authService.sync({});
      setUser(user);
      navigate('/setup');
    } catch (err) {
      setError(err.message || t('register.error'));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setGoogleLoading(true);
    setError('');
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/setup` },
    });
    if (err) {
      setError(err.message || t('login.error'));
      setGoogleLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface">
      <PublicNav hideAuth />
      <div className="flex items-center justify-center p-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-100">{t('register.title')}</h1>
          <p className="text-slate-400 mt-1">{t('register.subtitle')}</p>
        </div>

        {confirmSent ? (
          <div className="card text-center space-y-4">
            <div className="text-4xl">📬</div>
            <p className="text-slate-200 font-medium">{t('register.confirm_title')}</p>
            <p className="text-slate-400 text-sm">{t('register.confirm_sub')}</p>
            <Link to="/login" className="block text-brand-400 hover:text-brand-300 text-sm transition-colors mt-2">
              {t('login.title')} →
            </Link>
          </div>
        ) : (
        <div className="card space-y-4">
          {/* Google OAuth */}
          <button
            type="button"
            onClick={handleGoogle}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl border border-slate-600 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium transition-all disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
              <path d="M47.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h13.1c-.6 3-2.3 5.5-4.8 7.2v6h7.7c4.5-4.2 7.5-10.3 7.5-17.5z" fill="#4285F4"/>
              <path d="M24 48c6.5 0 12-2.1 16-5.8l-7.7-6c-2.2 1.5-5 2.3-8.3 2.3-6.4 0-11.8-4.3-13.7-10.1H2.3v6.2C6.3 42.6 14.6 48 24 48z" fill="#34A853"/>
              <path d="M10.3 28.4A14.4 14.4 0 0 1 9.7 24c0-1.5.3-3 .6-4.4v-6.2H2.3A23.9 23.9 0 0 0 0 24c0 3.9.9 7.5 2.3 10.6l8-6.2z" fill="#FBBC05"/>
              <path d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.9 2.4 30.4 0 24 0 14.6 0 6.3 5.4 2.3 13.4l8 6.2C12.2 13.8 17.6 9.5 24 9.5z" fill="#EA4335"/>
            </svg>
            {googleLoading ? t('login.loading') : t('auth.google')}
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-700" />
            <span className="text-slate-500 text-xs">{t('auth.or')}</span>
            <div className="flex-1 h-px bg-slate-700" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label={t('register.name')}
              placeholder="João Silva"
              value={form.full_name}
              onChange={(e) => set('full_name', e.target.value)}
              required
              autoFocus
            />
            <Input
              label={t('register.email')}
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              required
            />
            <Input
              label={t('register.password')}
              type="password"
              placeholder={t('register.password_hint')}
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              required
            />

            {error && (
              <div className="p-3 bg-red-900/20 border border-red-800 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" isLoading={loading}>
              {t('register.submit')}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500">
            {t('register.have_account')}{' '}
            <Link to="/login" className="text-brand-400 hover:text-brand-300 transition-colors">
              {t('register.sign_in')}
            </Link>
          </p>
        </div>
        )}
      </div>
      </div>
    </div>
  );
}
